<!-- app-subdomain-creators/src/Components/CreatePostNewsfeedModal.vue -->

<template>
  <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="props.isOpen"
      class="fixed inset-0 bg-white bg-opacity-75 flex justify-center items-center py-4 z-50"
      @click.self="close"
      @dragenter="handleModalDragEnter"
      @dragover="handleModalDragOver"
      @dragleave="handleModalDragLeave"
      @drop="handleModalDrop"
    >
      <div
        class="bg-white relative rounded-xl max-w-lg w-full mx-auto border-[1px] pt-3 pb-2 border-zinc-200 shadow-lg
               max-h-[90vh] px-0"
        @click.stop
      >
        <div
          v-if="isDropzoneActive"
          class="absolute inset-2 z-[70] pointer-events-none rounded-2xl border-2 border-dashed border-white/60 bg-black/45 backdrop-blur-[1px] flex items-center justify-center"
        >
          <p class="text-white text-2xl font-semibold tracking-tight">
            Drop photos or videos
          </p>
        </div>

        <!-- create post close button -->
        <button
          v-if="!showMoreBackgrounds && !showScheduler"
          @click="close"
          class="text-zinc-600 hover:text-zinc-800 absolute top-3 right-4 rounded-full bg-zinc-200 hover:bg-zinc-300 p-1"
        >
          <XMarkIcon class="w-7 h-7" />
        </button>

        <!-- back to create post button (backgrounds chooser) -->
        <button
          v-if="showMoreBackgrounds"
          @click="toggleMoreBackgrounds"
          class="text-gray-600 hover:text-gray-800 absolute top-3 left-4 rounded-full bg-zinc-200 hover:bg-zinc-300 p-2"
        >
          <ArrowLeftIcon class="w-5 h-5" />
        </button>

        <!-- back to create post button (scheduler step) -->
        <button
          v-if="showScheduler && !isEditMode"
          @click="showScheduler = false"
          class="text-gray-600 hover:text-gray-800 absolute top-3 left-4 rounded-full bg-zinc-200 hover:bg-zinc-300 p-2"
        >
          <ArrowLeftIcon class="w-5 h-5" />
        </button>

        <div class="flex items-center border-b border-zinc-200 pb-5 pt-2 w-full">
          <h3 class="text-xl font-semibold text-center mx-auto">
            {{
              showScheduler && !isEditMode
                ? 'Schedule post'
                : (!showMoreBackgrounds ? (isEditMode ? 'Edit post' : 'Create post') : 'Choose background')
            }}
          </h3>
        </div>

        <!-- MAIN BODY -->
        <div v-if="!showMoreBackgrounds">
          <!-- STEP 1: CREATE POST (original UI) -->
          <div v-if="!showScheduler">

            <!-- profile info -->
            <div class="flex items-center gap-2 px-4 pt-4 pb-2">
              <img
                class="w-10 h-10 rounded-full border border-zinc-200 object-cover"
                :src="props.postingAsAvatarUrl || defaultAvatarSrc"
                alt="Posting as avatar"
              />
              <div class="flex flex-col">
                <h4 class="text-zinc-700 text-base font-medium">
                  {{ props.postingAsName || 'You' }}
                </h4>

                <!-- badge directly under name -->
                <div
                  class="mt-1 inline-flex items-center gap-[3px] bg-zinc-200 rounded-md pl-1.5 py-[4px] w-28"
                >
                  <div
                    class="flex items-center justify-center w-4 h-4 bg-zinc-500 rounded-full"
                  >
                    <UserGroupIcon class="w-3 h-3 text-white" />
                  </div>
                  <p class="text-[12px] text-zinc-700 leading-none">
                    Private group
                  </p>
                </div>
              </div>
            </div>

            <!-- 🔹 SCROLL AREA: text + image -->
            <div
              class="chat-scroll w-full px-4 pt-2 pb-2 space-y-3
                     overflow-y-auto
                     max-h-[45vh] "
            >
              <!-- textarea / post content -->
              <div
                :class="[
                  'transition-all duration-150',
                  hasSelectedBackground ? 'relative rounded-none min-h-[347px] -mx-[17px] flex items-center justify-center' : 'rounded-xl'
                ]"
                :style="composerBackgroundStyle"
              >
                <div
                  ref="composerRef"
                  contenteditable="true"
                  :class="[
                    textAreaClass,
                    'composer-editor',
                    hasSelectedBackground ? 'composer-editor--with-bg' : '',
                    hasSelectedBackground && isComposerEmpty ? 'composer-editor--with-bg-empty' : '',
                    hasSelectedBackground ? 'px-0 py-0' : 'px-3 py-2',
                    hasSelectedBackground && selectedBackgroundTone === 'light' ? 'composer-editor--tone-dark' : '',
                    hasSelectedBackground && selectedBackgroundTone !== 'light' ? 'composer-editor--tone-light' : '',
                  ]"
                  class="w-full border-none rounded bg-transparent mb-0
                         focus:ring-0 focus:outline-none"
                  data-placeholder="Write Something..."
                  @input="handleComposerInput"
                  @blur="syncPostContentFromComposer"
                  @focus="handleComposerFocus"
                  @click="handleComposerClick"
                ></div>
              </div>

              <!-- media preview grid (images/videos) -->
              <div v-if="attachedPreviewItems.length" class="w-full">
                <div
                  class="overflow-hidden rounded-xl border border-zinc-100 grid gap-[2px]"
                  :class="previewGridClass"
                >
                  <div
                    v-for="(item, idx) in attachedPreviewItems"
                    :key="item.fileId || item.tempId || idx"
                    :class="previewTileClass(idx, attachedPreviewItems.length)"
                  >
                    <img
                      v-if="item.mediaKind !== 'video'"
                      :src="item.previewUrl"
                      alt="Selected image preview"
                      class="block w-full h-full object-cover"
                    />

                    <div
                      v-else
                      class="relative w-full h-full bg-black"
                    >
                      <video
                        v-if="item.isUploading || !item.isExisting"
                        :src="item.previewUrl"
                        class="block w-full h-full object-cover opacity-90"
                        muted
                        autoplay
                        loop
                        playsinline
                        preload="metadata"
                      ></video>
                      <img
                        v-else
                        :src="item.thumbnailUrl || item.previewUrl"
                        alt="Selected video preview"
                        class="block w-full h-full object-cover opacity-90"
                        @error="handleVideoPreviewThumbError(item)"
                      />
                      <div class="absolute inset-0 flex items-center justify-center">
                        <div class="rounded-full bg-black/65 p-3">
                          <PlayIcon class="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="item.isUploading"
                      class="absolute inset-0 bg-black/35 flex flex-col items-center justify-center gap-2"
                    >
                      <div class="w-6 h-6 rounded-full border-2 border-white/70 border-t-transparent animate-spin"></div>
                      <p class="text-[11px] font-semibold text-white">
                        Uploading...
                      </p>
                    </div>

                    <!-- small X to remove this image -->
                    <button
                      v-if="item.mediaKind !== 'video' && !item.isUploading"
                      type="button"
                      class="absolute top-2 left-2 z-20 inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/95 px-2.5 text-xs font-semibold text-zinc-800 shadow-sm ring-1 ring-black/5 transition hover:bg-white"
                      @click.stop="openImageEditor(idx)"
                    >
                      <LinkIcon class="h-4 w-4" />
                      Edit
                    </button>

                    <button
                      type="button"
                      class="absolute top-2 right-2 z-20 w-7 h-7 flex items-center justify-center
                             rounded-full bg-black/60 hover:bg-black/80 text-white text-xs"
                      @click.stop="removeImageAt(idx)"
                    >
                      ✕
                    </button>

                    <!-- +N overlay on last tile if there are more than 5 -->
                    <div
                      v-if="idx === attachedPreviewItems.length - 1 && extraAttachedCount > 0"
                      class="absolute inset-0 bg-black/45 flex items-center justify-center"
                    >
                      <span class="text-white text-2xl font-semibold">
                        +{{ extraAttachedCount }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- poll composer -->
              <div v-if="isPollEnabled" class="w-full border border-zinc-300 rounded-lg p-3">
                <div class="flex items-center justify-between mb-3">
                  <h4 class="text-zinc-900 text-base font-semibold">Add poll</h4>
                  <button
                    type="button"
                    class="w-8 h-8 rounded-full bg-zinc-200 hover:bg-zinc-300 text-zinc-600 flex items-center justify-center"
                    @click="disablePoll"
                  >
                    <XMarkIcon class="w-5 h-5" />
                  </button>
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(opt, idx) in pollOptions"
                    :key="`poll-opt-${idx}`"
                    class="flex items-center gap-2"
                  >
                    <input
                      v-model="pollOptions[idx]"
                      type="text"
                      class="w-full border border-zinc-300 rounded-xl px-3 py-3 focus:ring-0 focus:outline-none text-zinc-800"
                      :placeholder="`Option ${idx + 1}`"
                    />
                    <button
                      v-if="pollOptions.length > 2"
                      type="button"
                      class="w-8 h-8 rounded-full bg-zinc-200 hover:bg-zinc-300 text-zinc-600 flex items-center justify-center shrink-0"
                      @click="removePollOption(idx)"
                    >
                      <XMarkIcon class="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div class="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    class="flex-1 rounded-lg bg-zinc-200 hover:bg-zinc-300 text-zinc-800 font-medium py-2"
                    @click="addPollOption"
                  >
                    + Add option
                  </button>

                  <div
                    v-if="canConfigurePollSettings"
                    ref="pollSettingsMenuRef"
                    class="relative z-[120]"
                  >
                    <button
                      ref="pollSettingsMenuButtonRef"
                      type="button"
                      class="h-[42px] px-3 rounded-lg bg-zinc-200 hover:bg-zinc-300 text-zinc-800 flex items-center gap-1"
                      @click.stop="togglePollSettingsMenu"
                    >
                      <Cog6ToothIcon class="w-5 h-5" />
                      <ChevronDownIcon class="w-4 h-4" />
                    </button>
                  </div>

                  <Teleport to="body">
                    <div
                      v-if="pollSettingsMenuOpen && canConfigurePollSettings"
                      ref="pollSettingsDropdownRef"
                      class="fixed w-[390px] rounded-2xl border border-zinc-200 bg-white shadow-2xl z-[1000] p-3 space-y-3"
                      :style="pollSettingsMenuStyle"
                      @click.stop
                    >
                      <label class="flex items-center justify-between gap-3 text-[15px] text-zinc-800 select-none">
                        <span class="leading-snug text-zinc-800 font-medium">
                          Allow people to choose multiple answers
                        </span>
                        <span class="relative inline-flex items-center justify-center">
                          <input
                            type="checkbox"
                            class="peer sr-only"
                            v-model="pollAllowMultipleAnswers"
                            disabled
                          />
                          <span
                            class="h-6 w-6 rounded-md border border-zinc-300 bg-zinc-100 inline-flex items-center justify-center text-transparent opacity-60 cursor-not-allowed
                                   peer-checked:border-[#2D8CFF] peer-checked:bg-[#2D8CFF] peer-checked:text-white"
                          >
                            <CheckIcon class="w-4 h-4" />
                          </span>
                        </span>
                      </label>

                      <label class="flex items-center justify-between gap-3 text-[15px] text-zinc-800 cursor-pointer select-none">
                        <span class="leading-snug text-zinc-800 font-medium">
                          Allow anyone to add options
                        </span>
                        <span class="relative inline-flex items-center justify-center">
                          <input
                            type="checkbox"
                            class="peer sr-only"
                            v-model="pollAllowAnyoneToAddOptions"
                          />
                          <span
                            class="h-6 w-6 rounded-md border border-zinc-300 bg-white inline-flex items-center justify-center text-transparent transition
                                   peer-checked:border-[#2D8CFF] peer-checked:bg-[#2D8CFF] peer-checked:text-white"
                          >
                            <CheckIcon class="w-4 h-4" />
                          </span>
                        </span>
                      </label>

                      <label class="flex items-center justify-between gap-3 text-[15px] text-zinc-800 cursor-pointer select-none">
                        <span class="leading-snug text-zinc-800 font-medium">
                          Show poll results to voters
                        </span>
                        <span class="relative inline-flex items-center justify-center">
                          <input
                            type="checkbox"
                            class="peer sr-only"
                            v-model="pollShowResults"
                          />
                          <span
                            class="h-6 w-6 rounded-md border border-zinc-300 bg-white inline-flex items-center justify-center text-transparent transition
                                   peer-checked:border-[#2D8CFF] peer-checked:bg-[#2D8CFF] peer-checked:text-white"
                          >
                            <CheckIcon class="w-4 h-4" />
                          </span>
                        </span>
                      </label>
                    </div>
                  </Teleport>
                </div>
              </div>
            </div>

            <!-- background and emoji selector (moved to bottom of post area) -->
            <div class="w-full py-2 mb-3 px-4">
              <div class="flex justify-between items-center">
                <!-- background icon and array -->
                <div class="flex items-center gap-2">
                  <!-- background icon button open -->
                  <img
                    v-if="!showBackgroundArray"
                    @click="toggleBackgroundArray"
                    :class="[
                      'w-10 h-10',
                      canUseBackgrounds ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'
                    ]"
                    src="@/assets/media/images/SATP_Aa_square-2x.png"
                    alt=""
                  />
                  <!-- close array button -->
                  <div
                    v-if="showBackgroundArray"
                    @click="toggleBackgroundArray"
                    class="flex w-[40px] h-[40px] cursor-pointer shrink-0 p-[2px]"
                  >
                  <div class="flex items-center w-full h-full rounded-lg bg-zinc-200 shadow-sm justify-center">
                    <ChevronLeftIcon class="w-6 h-6 text-zinc-500" />
                    </div>
                  </div>

                  <!-- background selector array -->
                  <div v-if="showBackgroundArray" class="flex items-center gap-[6px]">
                    <button
                      type="button"
                      @click="selectBackground(null)"
                      class="relative w-[34px] h-[34px] rounded-lg border border-zinc-200 bg-zinc-100 shadow-sm shrink-0"
                      :class="!selectedBackgroundKey ? 'ring-2 ring-[#2D8CFF]' : ''"
                    ></button>

                    <button
                      v-for="bg in quickBackgroundOptions"
                      :key="`quick-bg-${bg.id}`"
                      type="button"
                      class="relative w-[34px] h-[34px] rounded-lg overflow-hidden border border-transparent shrink-0"
                      :class="selectedBackgroundKey === bg.id ? 'ring-2 ring-[#2D8CFF]' : ''"
                      @click="selectBackground(bg.id)"
                    >
                      <img
                        :src="bg.selectorUrl"
                        class="w-full h-full object-cover"
                        :alt="bg.id"
                      />
                    </button>

                    <button
                      type="button"
                      @click="toggleMoreBackgrounds"
                      class="flex items-center justify-center w-[34px] h-[34px] cursor-pointer rounded-lg bg-zinc-200 shadow-sm shrink-0"
                    >
                      <Squares2X2Icon class="w-6 h-6 text-zinc-700" />
                    </button>
                  </div>
                </div>

                <!-- Emoji Icon -->
                <div class="relative">
                  <FaceSmileIcon
                    @mouseenter="showChatEmojiTooltip = true"
                    @mouseleave="showChatEmojiTooltip = false"
                    @click="toggleEmojiPicker"
                    class="text-zinc-400 w-8 h-8 cursor-pointer"
                  />

                  <!-- Emoji tooltip -->
                  <div
                    v-if="showChatEmojiTooltip"
                    class="absolute text-center w-28 bottom-full p-2 -translate-x-20 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
                  >
                    Emoji
                    <div
                      class="tooltip-arrow absolute left-3/4 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
                    ></div>
                  </div>

                  <!-- Emoji Picker -->
                  <div
                    v-if="showEmojiPicker"
                    class="emoji-picker-container absolute bottom-[45px] -left-4 z-30"
                  >
                    <EmojiPicker @select="addEmoji" />
                  </div>
                </div>
              </div>
            </div>

            <!-- add to post selector -->
            <div class="px-4">
              <div
                class="w-full border border-zinc-300 rounded-lg p-3 mb-3 shadow-sm"
              >
                <div class="flex justify-between items-center">
                  <h4 class="text-zinc-800 text-sm font-medium">
                    Add to your post
                  </h4>

                  <div class="flex items-center gap-2">
                    <!-- Photo/Video button with tooltip -->
                    <div class="relative group/photo">
                      <button
                        type="button"
                        class="flex items-center justify-center rounded-full p-1"
                      :class="isMediaPickerDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-zinc-100'"
                      :disabled="isMediaPickerDisabled"
                      @click="triggerImagePicker"
                    >
                        <img
                          :src="photoVideoButtonIcon"
                          alt="Photo/Video"
                          class="w-6 h-6"
                        />
                      </button>

                      <!-- Pretty tooltip -->
                      <span
                        class="pointer-events-none absolute hidden group-hover/photo:block px-2 py-2 text-xs rounded-xl 
                               bg-black/75 text-white whitespace-nowrap z-50 -top-8 right-0 shadow-lg"
                      >
                        {{ isMediaPickerDisabled ? 'Background or poll selected' : 'Photo/Video' }}
                      </span>

                      <!-- Hidden file input -->
                      <input
                        ref="fileInputRef"
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        class="hidden"
                        @change="handleMediaSelectedWithPendingState"
                      />
                    </div>

                    <div class="relative group/poll">
                      <button
                        type="button"
                        class="flex items-center justify-center rounded-full p-1"
                        :class="isPollPickerDisabled ? 'opacity-40 cursor-not-allowed' : (isPollEnabled ? 'bg-zinc-100' : 'hover:bg-zinc-100')"
                        :disabled="isPollPickerDisabled"
                        @click="enablePoll"
                      >
                        <img
                          :src="pollButtonIcon"
                          alt="Poll"
                          class="w-6 h-6"
                        />
                      </button>

                      <span
                        class="pointer-events-none absolute hidden group-hover/poll:block px-2 py-2 text-xs rounded-xl 
                               bg-black/75 text-white whitespace-nowrap z-50 -top-8 right-0 shadow-lg"
                      >
                        Poll
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Schedule trigger + Post button -->
            <div class="flex w-full items-center gap-2 px-4 py-2">
          
              <!-- Main Post Button -->
            <div class="flex mb-3 w-full">
              <button
                :class="{
                  'bg-[#2D8CFF] hover:bg-[#1f7aeb] text-white': canSubmit && !isSubmitting,
                  'bg-zinc-200 cursor-not-allowed text-zinc-400': !canSubmit || isSubmitting
                }"
                class="font-medium text-base px-4 py-2 rounded-md w-full transition-colors duration-300"
                :disabled="!canSubmit || isSubmitting"
                @click="submitPost"
              >
                {{ isSubmitting ? submittingButtonLabel : submitButtonLabel }}
              </button>
            </div>

            <!-- Schedule Button -->
          <div v-if="!isEditMode" class="flex mb-3 items-center text-center">
            <div class="relative group">
              <button
                type="button"
                class="text-sm font-medium w-10 py-2 bg-zinc-200 rounded-md transition-colors duration-300"
                :class="{
                  'cursor-pointer hover:text-[#1f7aeb]': canSubmit && !isSubmitting,
                  'cursor-not-allowed text-zinc-400': !canSubmit || isSubmitting
                }"
                :disabled="!canSubmit || isSubmitting"
                @click="showScheduler = true"
              >
                <CalendarDaysIcon
                  class="w-6 text-center mx-auto"
                  :class="{
                    'cursor-pointer hover:text-[#1f7aeb] text-zinc-800': canSubmit && !isSubmitting,
                    'text-zinc-400': !canSubmit || isSubmitting
                  }"
                />
              </button>

              <!-- Tooltip -->
              <div
                class="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2
                      px-2 py-2 rounded-xl bg-black/75 text-xs text-white
                      opacity-0 group-hover:opacity-100 transition-opacity duration-200
                      whitespace-nowrap"
              >
                Schedule post
                
              </div>
            </div>
          </div>

            </div>

            <p v-if="pollValidationError" class="px-4 pb-2 text-sm text-red-500">
              {{ pollValidationError }}
            </p>


          </div>

          <!-- STEP 2: SCHEDULER UI (fills the modal body) -->
          <div v-else-if="!isEditMode">
            <div class="px-4 pt-4 pb-6">
              <p class="text-sm text-zinc-500 mb-4">
                Choose a date and time in the future when you want your post to be published.
              </p>

              <label class="mb-4 flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-700">
                <input
                  v-model="scheduleDeletionEnabled"
                  type="checkbox"
                  class="h-4 w-4 rounded border-zinc-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                />
                <span>Schedule a Deletion Date</span>
              </label>

              <div class="space-y-4">
                <div>
                  <h4
                    v-if="scheduleDeletionEnabled"
                    class="mb-2 text-sm font-semibold text-zinc-800"
                  >
                    Post Release Date
                  </h4>
                  <NewsfeedPostScheduler
                    :show-submit-button="false"
                    @selection-change="handleReleaseDateChange"
                  />
                </div>

                <div v-if="scheduleDeletionEnabled">
                  <h4 class="mb-2 text-sm font-semibold text-zinc-800">
                    Post Delete Date
                  </h4>
                  <NewsfeedPostScheduler
                    :show-submit-button="false"
                    @selection-change="handleDeleteDateChange"
                  />
                </div>
              </div>

              <p
                v-if="scheduleValidationError"
                class="mt-3 text-xs text-red-500"
              >
                {{ scheduleValidationError }}
              </p>

              <button
                type="button"
                class="mt-4 w-full rounded-md px-4 py-2 text-base font-semibold transition-colors duration-150"
                :class="canSubmitSchedule ? 'bg-[#2D8CFF] text-white hover:bg-[#1f7aeb]' : 'cursor-not-allowed bg-zinc-200 text-zinc-500'"
                :disabled="!canSubmitSchedule || isSubmitting"
                @click="handleSchedulePicked"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>

        <!-- show more backgrounds options -->
        <div v-if="showMoreBackgrounds" class="p-4 h-[428px] overflow-auto">
          <div
            v-for="group in backgroundGroups"
            :key="group.id"
            class="mb-8 last:mb-0"
          >
            <h4 class="text-zinc-800 font-medium text-base mb-4 pl-4">{{ group.label }}</h4>
            <div class="flex flex-wrap gap-3 justify-center items-center mx-auto">
              <button
                v-for="bg in group.items"
                :key="bg.id"
                type="button"
                class="cursor-pointer rounded-2xl ring-2 ring-transparent"
                :class="selectedBackgroundKey === bg.id ? 'ring-[#2D8CFF]' : ''"
                @click="selectBackground(bg.id)"
              >
                <img
                  class="w-20 h-20 cursor-pointer rounded-2xl object-cover"
                  :src="bg.selectorUrl"
                  :alt="bg.id"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="imageEditorOpen"
          class="fixed inset-0 z-[90] flex items-center justify-center bg-zinc-950/60 px-4 py-6 backdrop-blur-sm"
          @click.self="closeImageEditor"
          @keydown.esc="closeImageEditor"
        >
          <div class="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl" @click.stop>
            <div class="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.16em] text-[#2D8CFF]">Image settings</p>
                <h3 class="mt-1 text-xl font-semibold text-zinc-950">Make this image clickable</h3>
              </div>
              <button
                type="button"
                class="rounded-full bg-zinc-100 p-2 text-zinc-600 transition hover:bg-zinc-200 hover:text-zinc-900"
                aria-label="Close image settings"
                @click="closeImageEditor"
              >
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>

            <div class="grid gap-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div class="flex min-h-56 items-center justify-center bg-zinc-950 p-4">
                <img
                  v-if="editingImageItem"
                  :src="editingImageItem.previewUrl"
                  alt="Image being edited"
                  class="max-h-80 max-w-full rounded-lg object-contain"
                />
              </div>

              <div class="space-y-5 p-5 md:p-6">
                <div>
                  <label for="newsfeed-image-link" class="block text-sm font-semibold text-zinc-900">
                    Link
                  </label>
                  <p class="mt-1 text-xs leading-5 text-zinc-500">
                    Clicking this image will open this destination instead of the photo viewer.
                  </p>
                  <div
                    class="mt-3 flex items-center gap-2 rounded-xl border bg-white px-3 py-2.5 shadow-sm transition focus-within:border-[#2D8CFF] focus-within:ring-2 focus-within:ring-[#2D8CFF]/15"
                    :class="imageLinkError ? 'border-red-400' : 'border-zinc-300'"
                  >
                    <LinkIcon class="h-5 w-5 shrink-0 text-zinc-400" />
                    <input
                      id="newsfeed-image-link"
                      v-model="imageLinkDraft"
                      type="text"
                      inputmode="url"
                      autocomplete="url"
                      placeholder="https://example.com"
                      class="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-zinc-900 outline-none ring-0 placeholder:text-zinc-400 focus:ring-0"
                      @input="imageLinkError = ''"
                      @keydown.enter.prevent="saveImageEditor"
                    />
                  </div>
                  <p v-if="imageLinkError" class="mt-2 text-xs font-medium text-red-600">{{ imageLinkError }}</p>
                </div>

                <label class="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                  <span>
                    <span class="block text-sm font-semibold text-zinc-900">Open in a new tab</span>
                    <span class="mt-1 block text-xs leading-5 text-zinc-500">Recommended for links away from your member area.</span>
                  </span>
                  <input
                    v-model="imageOpenInNewTabDraft"
                    type="checkbox"
                    class="mt-0.5 h-5 w-5 rounded border-zinc-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                  />
                </label>

                <div class="flex items-center justify-between gap-3 pt-1">
                  <button
                    type="button"
                    class="text-sm font-semibold text-zinc-500 transition hover:text-zinc-900"
                    @click="clearImageLink"
                  >
                    Remove link
                  </button>
                  <div class="flex gap-2">
                    <button type="button" class="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50" @click="closeImageEditor">Cancel</button>
                    <button type="button" class="rounded-lg bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#1f7aeb]" @click="saveImageEditor">Done</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>


<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed, watch, defineProps, defineEmits } from 'vue';
import {
  XMarkIcon,
  FaceSmileIcon,
  ChevronLeftIcon,
  ArrowLeftIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  LinkIcon
} from '@heroicons/vue/24/outline';
import { Squares2X2Icon, UserGroupIcon, PlayIcon, Cog6ToothIcon, CheckIcon } from '@heroicons/vue/24/solid';

import { useMediaUploadStore } from '@/stores/mediaUploadStore';
import NewsfeedPostScheduler from '@/Components/NewsfeedPostScheduler.vue'
import {
  NEWSFEED_BACKGROUND_GROUPS,
  NEWSFEED_BACKGROUND_MAX_CHARS,
  detectNewsfeedBackgroundTone,
  getNewsfeedBackgroundById,
  guessNewsfeedBackgroundTone,
} from '@/utils/newsfeedPostBackgrounds';



import EmojiPicker from 'vue3-emoji-picker';
import 'vue3-emoji-picker/css';

// ⬇️ BFF proxy base URL
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

// ✅ CREATE STORE INSTANCE (this was missing)
const mediaUploadStore = useMediaUploadStore();

// Define props and emits
const props = defineProps({
  isOpen: Boolean,
  mode: {
    type: String,
    default: 'create',
  },
  initialPost: {
    type: Object,
    default: null,
  },
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },
  postingAsName: {
    type: String,
    default: 'You',
  },
  postingAsAvatarUrl: {
    type: String,
    default: null,
  },
  currentUserType: {
    type: String,
    default: 'creator',
  },
  startWithPoll: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'post-created', 'post-updated']);

const isEditMode = computed(() => String(props.mode || 'create') === 'edit');
const initialPostId = computed(() => {
  const id = props.initialPost?.id;
  return Number.isFinite(Number(id)) && Number(id) > 0 ? Number(id) : null;
});
const submitButtonLabel = computed(() => (isEditMode.value ? 'Save' : 'Post'));
const submittingButtonLabel = computed(() =>
  isEditMode.value ? 'Saving...' : 'Posting...'
);

// Close function to emit the close event to the parent
function close() {
  closeImageEditor();
  emit('close');
}


const defaultAvatarSrc = new URL(
  '@/assets/media/images/woman-avatar.jpg',
  import.meta.url
).href;


// Icon for Photo/Video button
const photoVideoButtonIcon = new URL(
  '@/assets/media/images/platforma-newsfeed-image.png',
  import.meta.url
).href;
const pollButtonIcon = new URL(
  '@/assets/media/images/video-image-newsfeed-poll-icon.png',
  import.meta.url
).href;

// Image selection + preview
const fileInputRef = ref(null);

// All attached media for this post
// Each item: { tempId, fileId, previewUrl, permanentUrl, isExisting, mediaKind, thumbnailUrl, isUploading, linkUrl, openInNewTab }
const attachedMedia = ref([]);
const initialExistingMediaFileIds = ref([]);
const editingImageIndex = ref(null);
const imageLinkDraft = ref('');
const imageOpenInNewTabDraft = ref(true);
const imageLinkError = ref('');
const imageEditorOpen = computed(() => Number.isInteger(editingImageIndex.value));
const editingImageItem = computed(() => (
  imageEditorOpen.value ? attachedMedia.value[editingImageIndex.value] || null : null
));
const isPollEnabled = ref(false);
const pollOptions = ref(['', '', '']);
const initialPollId = ref(null);
const pollSettingsMenuOpen = ref(false);
const pollSettingsMenuRef = ref(null);
const pollSettingsMenuButtonRef = ref(null);
const pollSettingsDropdownRef = ref(null);
const pollSettingsMenuStyle = ref({
  top: '0px',
  left: '0px',
});
const pollAllowMultipleAnswers = ref(false);
const pollAllowAnyoneToAddOptions = ref(false);
const pollShowResults = ref(false);
const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.avif'];
const videoFileExtensions = ['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi', '.ogv'];

const pollAuthorUserType = computed(() => {
  const initialType =
    props.initialPost?.authorUserType ||
    props.initialPost?.author_user_type ||
    null;
  const activeType = isEditMode.value ? initialType : props.currentUserType;
  return String(activeType || 'creator').trim().toLowerCase();
});

const isCreatorPollAuthor = computed(() => pollAuthorUserType.value === 'creator');
const canConfigurePollSettings = computed(() => isCreatorPollAuthor.value);

function normalizeAttachmentFileIds(post) {
  const atts = Array.isArray(post?.attachments) ? post.attachments : [];
  const normalized = atts
    .map((att) => {
      if (!att) return null;
      if (att.fileId) return String(att.fileId);
      if (att.file_id) return String(att.file_id);
      if (att.mediaFileId) return String(att.mediaFileId);
      return null;
    })
    .filter(Boolean);

  const coverMediaFileId =
    post?.coverMediaFileId || post?.cover_media_file_id || null;
  if (coverMediaFileId) normalized.unshift(String(coverMediaFileId));

  return Array.from(new Set(normalized));
}

function normalizePollForEdit(post) {
  const source = post?.poll || post?.pollData || post?.postPoll || null;
  const isPollPost = source || post?.postType === 'poll' || post?.post_type === 'poll';
  if (!isPollPost) return null;

  const base = source || {};
  const rawOptions =
    base.options ||
    base.pollOptions ||
    post?.pollOptions ||
    [];

  const options = (Array.isArray(rawOptions) ? rawOptions : [])
    .map((o) => String(o?.text || o?.optionText || o?.option_text || o || '').trim())
    .filter(Boolean);

  const normalizedOptions = options.length ? options : ['', ''];
  while (normalizedOptions.length < 2) normalizedOptions.push('');

  const allowMultipleAnswers = !!(
    base?.allowMultipleAnswers ??
    base?.allow_multiple_answers ??
    post?.allowMultipleAnswers ??
    post?.allow_multiple_answers ??
    false
  );
  const allowAnyoneToAddOptions = !!(
    base?.allowAnyoneToAddOptions ??
    base?.allow_anyone_to_add_options ??
    post?.allowAnyoneToAddOptions ??
    post?.allow_anyone_to_add_options ??
    false
  );
  const showResults = !!(
    base?.showResults ??
    base?.show_results ??
    post?.showResults ??
    post?.show_results ??
    false
  );

  return {
    id: base?.id || base?.pollId || base?.poll_id || null,
    options: normalizedOptions,
    allowMultipleAnswers,
    allowAnyoneToAddOptions,
    showResults,
  };
}

function applyPollSettingsForContext(settings = null) {
  const defaults = {
    allowMultipleAnswers: false,
    allowAnyoneToAddOptions: false,
    showResults: false,
  };
  const next = { ...defaults, ...(settings || {}) };

  if (!isCreatorPollAuthor.value) {
    pollAllowMultipleAnswers.value = false;
    pollAllowAnyoneToAddOptions.value = false;
    pollShowResults.value = false;
    return;
  }

  // Reserved for future use; always off for now.
  pollAllowMultipleAnswers.value = false;
  pollAllowAnyoneToAddOptions.value = !!next.allowAnyoneToAddOptions;
  pollShowResults.value = !!next.showResults;
}

function triggerImagePicker() {
  if (isMediaPickerDisabled.value) return;
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
}

function resetFileInput() {
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
}

function removeImageAt(index) {
  const item = attachedMedia.value[index];
  if (item?.previewUrl && !item?.isExisting) {
    URL.revokeObjectURL(item.previewUrl);
  }
  if (editingImageIndex.value === index) closeImageEditor();
  attachedMedia.value.splice(index, 1);
  resetFileInput();
}

function normalizeImageLink(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;

  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const parsed = new URL(candidate);
    if (!['http:', 'https:'].includes(parsed.protocol)) return '';
    return parsed.toString();
  } catch (_) {
    return '';
  }
}

function openImageEditor(index) {
  const item = attachedMedia.value[index];
  if (!item || item.mediaKind === 'video' || item.isUploading) return;
  editingImageIndex.value = index;
  imageLinkDraft.value = String(item.linkUrl || '');
  imageOpenInNewTabDraft.value = item.openInNewTab !== false;
  imageLinkError.value = '';
}

function closeImageEditor() {
  editingImageIndex.value = null;
  imageLinkDraft.value = '';
  imageOpenInNewTabDraft.value = true;
  imageLinkError.value = '';
}

function clearImageLink() {
  imageLinkDraft.value = '';
  imageOpenInNewTabDraft.value = true;
  imageLinkError.value = '';
}

function saveImageEditor() {
  const item = editingImageItem.value;
  if (!item) return;

  const rawLink = String(imageLinkDraft.value || '').trim();
  const normalizedLink = normalizeImageLink(rawLink);
  if (rawLink && !normalizedLink) {
    imageLinkError.value = 'Enter a valid web address or an internal path beginning with /.';
    return;
  }

  item.linkUrl = normalizedLink || null;
  item.openInNewTab = normalizedLink ? !!imageOpenInNewTabDraft.value : true;
  closeImageEditor();
}

function inferMediaKindFromAttachment(att) {
  const mime = String(att?.mimeType || att?.mime || att?.contentType || '').toLowerCase().trim();
  const fileType = String(att?.fileType || att?.type || '').toLowerCase().trim();
  const url = String(att?.url || att?.secureVideoUrl || att?.videoUrl || att?.secureImageUrl || att?.imageUrl || '').toLowerCase();

  if (mime.startsWith('video/') || fileType === 'video') return 'video';
  if (mime.startsWith('image/') || fileType === 'image') return 'image';
  if (videoFileExtensions.some((ext) => url.includes(ext))) return 'video';
  if (imageFileExtensions.some((ext) => url.includes(ext))) return 'image';
  if (att?.secureVideoUrl || att?.videoUrl || att?.thumbnailUrl || att?.posterUrl) return 'video';
  return 'image';
}

function handleVideoPreviewThumbError(item) {
  if (!item || item.mediaKind !== 'video') return;
  // If thumbnail endpoint is not ready yet, fall back to local preview URL.
  item.thumbnailUrl = item.previewUrl || null;
}

function clearAllImages() {
  closeImageEditor();
  attachedMedia.value.forEach((item) => {
    if (item.previewUrl && !item.isExisting) {
      URL.revokeObjectURL(item.previewUrl);
    }
  });
  attachedMedia.value = [];
  initialExistingMediaFileIds.value = [];
  resetFileInput();
}

function resetPollState() {
  isPollEnabled.value = false;
  pollOptions.value = ['', '', ''];
  initialPollId.value = null;
  pollSettingsMenuOpen.value = false;
  applyPollSettingsForContext(null);
}

function enablePoll() {
  if (isPollPickerDisabled.value) return;
  isPollEnabled.value = true;
  if (attachedMedia.value.length) {
    clearAllImages();
  }
  if (!Array.isArray(pollOptions.value) || pollOptions.value.length < 3) {
    pollOptions.value = ['', '', ''];
  }
  applyPollSettingsForContext({
    allowMultipleAnswers: pollAllowMultipleAnswers.value,
    allowAnyoneToAddOptions: pollAllowAnyoneToAddOptions.value,
    showResults: pollShowResults.value,
  });
}

function disablePoll() {
  resetPollState();
}

function addPollOption() {
  if (pollOptions.value.length >= 10) return;
  pollOptions.value.push('');
}

function togglePollSettingsMenu() {
  if (!canConfigurePollSettings.value) return;
  pollSettingsMenuOpen.value = !pollSettingsMenuOpen.value;
  if (pollSettingsMenuOpen.value) {
    nextTick(() => {
      positionPollSettingsMenu();
    });
  }
}

function positionPollSettingsMenu() {
  if (!pollSettingsMenuOpen.value) return;
  const btn = pollSettingsMenuButtonRef.value;
  if (!btn) return;

  const rect = btn.getBoundingClientRect();
  const menuWidth = 340;
  const viewportPadding = 12;
  const gap = 8;

  let left = rect.left;
  if (left < viewportPadding) left = viewportPadding;
  if (left + menuWidth > window.innerWidth - viewportPadding) {
    left = Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding);
  }

  let top = rect.bottom + gap;
  const estimatedMenuHeight = 190;
  if (top + estimatedMenuHeight > window.innerHeight - viewportPadding) {
    top = Math.max(viewportPadding, rect.top - estimatedMenuHeight - gap);
  }

  pollSettingsMenuStyle.value = {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
  };
}

function removePollOption(index) {
  if (pollOptions.value.length <= 2) return;
  pollOptions.value.splice(index, 1);
}

const isDropzoneActive = ref(false);
const dragDepth = ref(0);

function dragEventHasFiles(event) {
  const types = Array.from(event?.dataTransfer?.types || []);
  return types.includes('Files');
}

function handleModalDragEnter(event) {
  event.preventDefault();
  event.stopPropagation();
  if (isMediaPickerDisabled.value) return;
  if (!dragEventHasFiles(event)) return;
  dragDepth.value += 1;
  isDropzoneActive.value = true;
}

function handleModalDragOver(event) {
  event.preventDefault();
  event.stopPropagation();
  if (isMediaPickerDisabled.value) return;
  if (!dragEventHasFiles(event)) return;
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  isDropzoneActive.value = true;
}

function handleModalDragLeave(event) {
  event.preventDefault();
  event.stopPropagation();
  if (isMediaPickerDisabled.value) return;
  if (!dragEventHasFiles(event)) return;
  dragDepth.value = Math.max(0, dragDepth.value - 1);
  if (dragDepth.value === 0) {
    isDropzoneActive.value = false;
  }
}

async function handleModalDrop(event) {
  event.preventDefault();
  event.stopPropagation();
  isDropzoneActive.value = false;
  dragDepth.value = 0;

  if (isMediaPickerDisabled.value) return;

  const files = Array.from(event?.dataTransfer?.files || []);
  if (!files.length) return;

  await processSelectedMediaFiles(files);
}

async function processSelectedMediaFiles(files) {
  const mediaFiles = files.filter((file) => {
    const mime = String(file?.type || '').toLowerCase();
    return mime.startsWith('image/') || mime.startsWith('video/');
  });
  if (!mediaFiles.length) {
    resetFileInput();
    return;
  }

  const MAX_ATTACHMENTS = 10;
  const remainingSlots = MAX_ATTACHMENTS - attachedMedia.value.length;
  if (remainingSlots <= 0) {
    resetFileInput();
    return;
  }

  // Cap any single drop/select action to remaining slots (max 10 total on post).
  const filesToUse = mediaFiles.slice(0, remainingSlots);

  for (const [fileIndex, file] of filesToUse.entries()) {
    const previewUrl = URL.createObjectURL(file);
    const tempId = `pending-${Date.now()}-${fileIndex}-${Math.random().toString(36).slice(2, 8)}`;
    const inferredType =
      String(file?.type || '').toLowerCase().startsWith('video/')
        ? 'video'
        : 'image';

    attachedMedia.value.push({
      tempId,
      fileId: null,
      previewUrl,
      permanentUrl: null,
      isExisting: false,
      mediaKind: inferredType,
      thumbnailUrl: inferredType === 'video' ? previewUrl : null,
      isUploading: true,
      linkUrl: null,
      openInNewTab: true,
    });

    const result = await mediaUploadStore.uploadSingleFile(file, null, {
      origin: 'newsfeed',
    });

    const itemIndex = attachedMedia.value.findIndex((item) => item?.tempId === tempId);
    if (itemIndex === -1) {
      URL.revokeObjectURL(previewUrl);
      continue;
    }

    if (result && result.fileId) {
      const uploadFileType = String(result.fileType || result.type || '').toLowerCase().trim();
      const uploadMimeType = String(result.mimeType || result.mime || '').toLowerCase().trim();
      const isVideoUpload = uploadFileType === 'video' || uploadMimeType.startsWith('video/');

      attachedMedia.value[itemIndex] = {
        ...attachedMedia.value[itemIndex],
        fileId: String(result.fileId),
        previewUrl,
        permanentUrl: result.permanentUrl || null,
        isExisting: false,
        mediaKind: isVideoUpload ? 'video' : 'image',
        thumbnailUrl: isVideoUpload
          ? (result.thumbnailUrl || `${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(String(result.fileId))}`)
          : null,
        isUploading: false,
      };
    } else {
      URL.revokeObjectURL(previewUrl);
      attachedMedia.value.splice(itemIndex, 1);
      console.warn('[CreatePostNewsfeedModal] Image upload failed');
    }
  }

  resetFileInput();
}

async function handleMediaSelectedWithPendingState(event) {
  if (isMediaPickerDisabled.value) {
    resetFileInput();
    return;
  }

  const files = Array.from(event?.target?.files || []);
  if (!files.length) return;
  await processSelectedMediaFiles(files);
}


// Emoji Picker
const showEmojiPicker = ref(false);
const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};

// Tooltips
const showChatEmojiTooltip = ref(false);

// Composer / content
const composerRef = ref(null);
const postContent = ref('');
// You can keep an initial height so the box isn't tiny
const height = ref(4 * 24); // 4 lines * 24px, adjust if you want

const textAreaClass = computed(() => {
  return postContent.value.length > NEWSFEED_BACKGROUND_MAX_CHARS ? 'text-[15px]' : 'text-2xl';
});

// placeholder for which background is chosen (string key)
const selectedBackgroundKey = ref(null);

const backgroundGroups = NEWSFEED_BACKGROUND_GROUPS;
const quickBackgroundOptions = computed(() => backgroundGroups.flatMap((g) => g.items).slice(0, 7));
const selectedBackground = computed(() => getNewsfeedBackgroundById(selectedBackgroundKey.value));
const hasSelectedBackground = computed(() => !!selectedBackground.value);
const selectedBackgroundTone = ref('dark');
const isComposerEmpty = computed(() => !String(postContent.value || '').trim());
const isBackgroundTextTooLong = computed(
  () => String(postContent.value || '').length > NEWSFEED_BACKGROUND_MAX_CHARS
);
const canUseBackgrounds = computed(
  () => !isPollEnabled.value && attachedMedia.value.length === 0 && !isBackgroundTextTooLong.value
);
const isMediaPickerDisabled = computed(() => isPollEnabled.value || hasSelectedBackground.value);
const isPollPickerDisabled = computed(() => hasSelectedBackground.value);
const composerBackgroundStyle = computed(() => {
  if (!hasSelectedBackground.value) return {};
  return {
    backgroundImage: `url(${selectedBackground.value.backgroundUrl})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  };
});

const isAnyMediaUploading = computed(() =>
  attachedMedia.value.some((item) => !!item?.isUploading)
);

const pollReadyOptions = computed(() =>
  pollOptions.value
    .map((o) => String(o || '').trim())
    .filter((o) => o.length > 0)
);

const pollValidationError = computed(() => {
  if (!isPollEnabled.value) return '';
  if (!postContent.value.trim()) return "You can't create a poll without text in your post.";
  if (pollReadyOptions.value.length < 2) return 'Please add at least 2 poll options.';
  return '';
});

const canSubmit = computed(() => {
  const hasReadyMedia = attachedMedia.value.some((m) => String(m?.fileId || '').trim().length > 0);
  if (isPollEnabled.value) {
    return !isAnyMediaUploading.value && !pollValidationError.value;
  }
  return !isAnyMediaUploading.value && (!!postContent.value.trim() || hasReadyMedia);
});

const currentMediaFileIds = computed(() =>
  attachedMedia.value
    .map((m) => String(m?.fileId || '').trim())
    .filter(Boolean)
);

const attachmentSettingsPayload = computed(() =>
  attachedMedia.value
    .filter((item) => item?.mediaKind !== 'video' && String(item?.fileId || '').trim())
    .map((item) => ({
      fileId: String(item.fileId),
      linkUrl: String(item.linkUrl || '').trim() || null,
      openInNewTab: item.openInNewTab !== false,
    }))
);

const mediaAddFileIds = computed(() => {
  const initialSet = new Set(initialExistingMediaFileIds.value.map(String));
  return currentMediaFileIds.value.filter((id) => !initialSet.has(String(id)));
});

const mediaRemoveFileIds = computed(() => {
  const currentSet = new Set(currentMediaFileIds.value.map(String));
  return initialExistingMediaFileIds.value.filter(
    (id) => !currentSet.has(String(id))
  );
});


const autoExpand = () => {
  nextTick(() => {
    const el = composerRef.value;
    if (!el) return;
    if (hasSelectedBackground.value) {
      el.style.height = 'auto';
      el.style.overflowY = 'hidden';
      return;
    }

    // Let it grow naturally to its content, but keep default composer height floor.
    el.style.height = 'auto';
    const MIN_COMPOSER_HEIGHT = 96;
    const newHeight = Math.max(el.scrollHeight, MIN_COMPOSER_HEIGHT);

    el.style.height = `${newHeight}px`;
    el.style.overflowY = 'hidden'; // ✅ never show inner scrollbar

    height.value = newHeight;
  });
};

// Background selector state (we’ll later wire actual background keys)
function escapeHtml(raw) {
  return String(raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function linkifyComposerText(raw) {
  const escaped = escapeHtml(raw).replace(/\r?\n/g, '<br>');
  const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;

  return escaped.replace(urlRegex, (fullMatch) => {
    let match = fullMatch;
    let trailing = '';
    while (/[),.!?:;]+$/.test(match)) {
      trailing = match.slice(-1) + trailing;
      match = match.slice(0, -1);
    }
    const href = match.startsWith('www.') ? `https://${match}` : match;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-[#2D8CFF] font-medium hover:underline break-all">${match}</a>${trailing}`;
  });
}

function getCaretOffset(element) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return 0;
  const range = selection.getRangeAt(0);
  const pre = range.cloneRange();
  pre.selectNodeContents(element);
  pre.setEnd(range.endContainer, range.endOffset);
  return pre.toString().length;
}

function setCaretOffset(element, chars) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  let remaining = Math.max(0, chars);

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.nodeValue?.length || 0;
      if (remaining <= len) {
        range.setStart(node, remaining);
        return true;
      }
      remaining -= len;
      return false;
    }
    for (const child of node.childNodes) {
      if (walk(child)) return true;
    }
    return false;
  }

  if (!walk(element)) {
    range.selectNodeContents(element);
    range.collapse(false);
  }

  selection.removeAllRanges();
  selection.addRange(range);
}

function syncPostContentFromComposer() {
  const el = composerRef.value;
  if (!el) return;
  const normalized = String(el.innerText || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r/g, '');
  const hasVisibleChars = normalized.replace(/\n/g, '').trim().length > 0;
  postContent.value = hasVisibleChars ? normalized : '';
}

function syncComposerFromPostContent(preserveCaret = false) {
  const el = composerRef.value;
  if (!el) return;
  const caret = preserveCaret ? getCaretOffset(el) : null;
  const html = linkifyComposerText(postContent.value || '');
  if (el.innerHTML !== html) {
    el.innerHTML = html;
    if (preserveCaret && caret != null) {
      setCaretOffset(el, caret);
    }
  }
  autoExpand();
}

function handleComposerInput() {
  syncPostContentFromComposer();
  syncComposerFromPostContent(true);
}

function handleComposerClick(event) {
  const anchor = event?.target?.closest ? event.target.closest('a') : null;
  if (!anchor) return;
  event.preventDefault();
  window.open(anchor.getAttribute('href'), '_blank', 'noopener,noreferrer');
}

function handleComposerFocus() {
  if (!hasSelectedBackground.value) return;
  const el = composerRef.value;
  if (!el) return;
  autoExpand();
  const normalized = String(el.innerText || '')
    .replace(/\u00a0/g, ' ')
    .trim();
  if (!normalized) {
    el.innerHTML = '';
    postContent.value = '';
    nextTick(() => {
      setCaretOffset(el, 0);
    });
  }
}

const showBackgroundArray = ref(false);

const toggleBackgroundArray = () => {
  if (!canUseBackgrounds.value) return;
  showBackgroundArray.value = !showBackgroundArray.value;
};

// choose-from-more backgrounds
const showMoreBackgrounds = ref(false);

const toggleMoreBackgrounds = () => {
  if (!canUseBackgrounds.value) return;
  showMoreBackgrounds.value = !showMoreBackgrounds.value;
};

function selectBackground(backgroundId) {
  if (backgroundId && !canUseBackgrounds.value) return;
  selectedBackgroundKey.value = backgroundId ? String(backgroundId) : null;
  showMoreBackgrounds.value = false;
  showBackgroundArray.value = true;
}

// Emoji picker → append emoji to content
const addEmoji = (emoji) => {
  // vue3-emoji-picker returns { i: '😊', ... }
  const ch = emoji && typeof emoji === 'object' && emoji.i ? emoji.i : String(emoji || '');
  postContent.value += ch;
  syncComposerFromPostContent(false);
  nextTick(() => {
    const el = composerRef.value;
    if (!el) return;
    el.focus();
    setCaretOffset(el, String(postContent.value || '').length);
  });
};

// ⬇️ Create/edit post via BFF proxy
const isSubmitting = ref(false);
const showScheduler = ref(false);

// Stores the chosen schedule datetime (SQL string: "YYYY-MM-DD HH:mm:ss")
const scheduledFor = ref(null);
const scheduleDeletionEnabled = ref(false);
const scheduledDeleteAt = ref(null);

function parseSchedulerUtcValue(value) {
  if (!value || typeof value !== 'string') return null;
  const parsed = new Date(`${value.replace(' ', 'T')}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const scheduleValidationError = computed(() => {
  if (!scheduleDeletionEnabled.value) return '';

  const releaseDate = parseSchedulerUtcValue(scheduledFor.value);
  const deleteDate = parseSchedulerUtcValue(scheduledDeleteAt.value);
  if (!releaseDate || !deleteDate) return '';

  if (deleteDate.getTime() <= releaseDate.getTime()) {
    return 'Post Delete Date must be later than the Post Release Date.';
  }

  return '';
});

const canSubmitSchedule = computed(() => {
  if (!scheduledFor.value) return false;
  if (!scheduleDeletionEnabled.value) return true;
  return !!scheduledDeleteAt.value && !scheduleValidationError.value;
});

function handleReleaseDateChange(datetimeSql) {
  scheduledFor.value = datetimeSql || null;
}

function handleDeleteDateChange(datetimeSql) {
  scheduledDeleteAt.value = datetimeSql || null;
}

watch(scheduleDeletionEnabled, (enabled) => {
  if (!enabled) scheduledDeleteAt.value = null;
});

function normalizeEditedPostResponse(data) {
  const post = data?.post || data?.updatedPost || data?.result || null;
  if (post) return post;

  if (initialPostId.value) {
    return {
      ...(props.initialPost || {}),
      id: initialPostId.value,
      body: postContent.value.trim(),
      content: postContent.value.trim(),
      backgroundKey: selectedBackgroundKey.value || null,
      coverMediaFileId: currentMediaFileIds.value[0] || null,
      attachments: currentMediaFileIds.value.map((fileId) => {
        const settings = attachmentSettingsPayload.value.find((item) => item.fileId === fileId);
        return settings ? { ...settings } : { fileId };
      }),
      poll: isPollEnabled.value
        ? {
            id: initialPollId.value || null,
            question: postContent.value.trim(),
            allowMultipleAnswers: false,
            allowAnyoneToAddOptions: canConfigurePollSettings.value
              ? !!pollAllowAnyoneToAddOptions.value
              : false,
            showResults: canConfigurePollSettings.value
              ? !!pollShowResults.value
              : false,
            options: pollReadyOptions.value.map((text, idx) => ({
              id: `tmp-${idx}`,
              text,
              voteCount: 0,
              percent: 0,
              isVotedByMe: false,
            })),
            totalVotes: 0,
            myVoteOptionId: null,
          }
        : null,
    };
  }

  return null;
}

function buildPostPayload(extraBody = {}) {
  const trimmed = postContent.value.trim();
  const mediaFileIdsPayload = currentMediaFileIds.value;
  const coverMediaFileId =
    mediaFileIdsPayload.length > 0 ? mediaFileIdsPayload[0] : null;

  const payload = {
    featureInstanceId: props.featureInstanceId,
    content: trimmed,
    backgroundKey: selectedBackgroundKey.value,
    coverMediaFileId,
    mediaFileIds: mediaFileIdsPayload,
    attachmentSettings: attachmentSettingsPayload.value,
    postedAsName: props.postingAsName || null,
    postedAsAvatarUrl: props.postingAsAvatarUrl || null,
    ...extraBody,
  };

  if (isPollEnabled.value) {
    payload.postType = 'poll';
    payload.mediaFileIds = [];
    payload.attachmentSettings = [];
    payload.coverMediaFileId = null;
    payload.poll = {
      id: initialPollId.value || undefined,
      question: trimmed,
      options: pollReadyOptions.value,
      allowMultipleAnswers: false,
      allowAnyoneToAddOptions: canConfigurePollSettings.value
        ? !!pollAllowAnyoneToAddOptions.value
        : false,
      showResults: canConfigurePollSettings.value
        ? !!pollShowResults.value
        : false,
    };
  } else if (isEditMode.value && props.initialPost?.poll) {
    // Explicitly clear poll if user disabled it while editing.
    payload.poll = null;
  }

  if (isEditMode.value) {
    payload.mediaAddFileIds = mediaAddFileIds.value;
    payload.mediaRemoveFileIds = mediaRemoveFileIds.value;
    if (isPollEnabled.value) {
      payload.mediaAddFileIds = [];
      payload.mediaRemoveFileIds = initialExistingMediaFileIds.value.slice();
    }
  }

  return payload;
}

async function submitPostCore({ endpointPath, method = 'POST', extraBody = {} }) {
  const trimmed = postContent.value.trim();
  const hasMedia = currentMediaFileIds.value.length > 0;

  if (isSubmitting.value) return null;
  if (isPollEnabled.value) {
    if (pollValidationError.value) return null;
  } else if (!trimmed && !hasMedia) {
    // require EITHER text OR one+ images
    return null;
  }

  if (!props.featureInstanceId) {
    console.warn(
      '[CreatePostNewsfeedModal] Missing featureInstanceId – cannot submit post'
    );
    alert('Cannot submit post: missing feature instance ID.');
    return null;
  }

  if (isEditMode.value && !initialPostId.value) {
    console.warn('[CreatePostNewsfeedModal] Missing post id for edit mode');
    alert('Cannot edit post: missing post ID.');
    return null;
  }

  isSubmitting.value = true;

  try {
    const payload = buildPostPayload(extraBody);
    const response = await fetch(`${apiProxyUrl}${endpointPath}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      data = null;
    }

    if (!response.ok || !data || !data.success) {
      console.error('[CreatePostNewsfeedModal] submit post failed:', {
        status: response.status,
        data,
        mode: props.mode,
      });
      alert(
        (data && data.error) ||
          'There was a problem saving the post. Please try again.'
      );
      return null;
    }

    if (isEditMode.value) {
      emit('post-updated', normalizeEditedPostResponse(data));
    } else {
      const createdPost = { ...(data.post || {}) };
      if (isPollEnabled.value) {
        const existingPoll = createdPost.poll || {};
        createdPost.postType = createdPost.postType || 'poll';
        createdPost.poll = {
          ...existingPoll,
          id: existingPoll.id || initialPollId.value || null,
          question: existingPoll.question || postContent.value.trim(),
          allowMultipleAnswers: false,
          allowAnyoneToAddOptions: canConfigurePollSettings.value
            ? !!pollAllowAnyoneToAddOptions.value
            : false,
          showResults: canConfigurePollSettings.value
            ? !!pollShowResults.value
            : false,
          options: Array.isArray(existingPoll.options) && existingPoll.options.length
            ? existingPoll.options
            : pollReadyOptions.value.map((text, idx) => ({
                id: `tmp-${idx}`,
                text,
                voteCount: 0,
                percent: 0,
                isVotedByMe: false,
              })),
          totalVotes: Number(existingPoll.totalVotes || 0) || 0,
          myVoteOptionId: existingPoll.myVoteOptionId || null,
        };
      }
      emit('post-created', createdPost);
    }

    postContent.value = '';
    selectedBackgroundKey.value = null;
    clearAllImages();
    resetPollState();
    showScheduler.value = false;
    scheduledFor.value = null;
    scheduleDeletionEnabled.value = false;
    scheduledDeleteAt.value = null;

    close();
    return isEditMode.value ? normalizeEditedPostResponse(data) : data.post;
  } catch (err) {
    console.error('[CreatePostNewsfeedModal] network error submitting post:', err);
    alert('Network error while saving the post. Please try again.');
    return null;
  } finally {
    isSubmitting.value = false;
  }
}

async function submitPost() {
  if (isEditMode.value) {
    await submitPostCore({
      endpointPath: `/api/creator-newsfeed/posts/${encodeURIComponent(initialPostId.value)}`,
      method: 'PATCH',
    });
    return;
  }

  await submitPostCore({
    endpointPath: '/api/creator-newsfeed/posts',
    method: 'POST',
  });
}

async function handleSchedulePicked() {
  if (isEditMode.value) return;

  if (!canSubmitSchedule.value) return;

  // Use the scheduled endpoint and include scheduledFor
  await submitPostCore({
    endpointPath: '/api/creator-newsfeed/posts/scheduled',
    method: 'POST',
    extraBody: {
      scheduledFor: scheduledFor.value,
      scheduledDeleteAt: scheduleDeletionEnabled.value
        ? scheduledDeleteAt.value
        : null,
    },
  });
}

const MAX_PREVIEW_TILES = 5;

const attachedPreviewItems = computed(() =>
  attachedMedia.value.slice(0, MAX_PREVIEW_TILES)
);

const extraAttachedCount = computed(() =>
  attachedMedia.value.length > MAX_PREVIEW_TILES
    ? attachedMedia.value.length - MAX_PREVIEW_TILES
    : 0
);

// Very simple Facebook-style layout
const previewGridClass = computed(() => {
  const count = attachedPreviewItems.value.length;

  if (count === 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-2 grid-rows-2';
  if (count === 4) return 'grid-cols-2 grid-rows-2';
  // 5 or more → 3 columns, 2 rows (we only show 5)
  return 'grid-cols-3 grid-rows-2';
});

function previewTileClass(index, count) {
  // All tiles are relatively positioned to host the X / +N overlays
  // plus some special spanning for more interesting layouts
  if (count === 1) return 'relative';
  if (count === 2) return 'relative aspect-[4/3]';

  if (count === 3) {
    // Big image on the left, two stacked on the right
    if (index === 0) return 'relative row-span-2';
    return 'relative';
  }

  if (count === 4) {
    // Simple 2x2 grid
    return 'relative';
  }

  if (count >= 5) {
    // First image large, others smaller on the right / bottom
    if (index === 0) return 'relative col-span-2 row-span-2';
    return 'relative';
  }

  return 'relative';
}


function resetComposerState() {
  postContent.value = '';
  selectedBackgroundKey.value = null;
  clearAllImages();
  resetPollState();
  showMoreBackgrounds.value = false;
  showBackgroundArray.value = false;
  showScheduler.value = false;
  scheduledFor.value = null;
  scheduleDeletionEnabled.value = false;
  scheduledDeleteAt.value = null;
  showEmojiPicker.value = false;
}

function hydrateEditComposer() {
  const post = props.initialPost || {};
  const initialText = post.body ?? post.content ?? '';
  const initialBackground = post.backgroundKey ?? post.backgroundColor ?? null;
  const existingFileIds = normalizeAttachmentFileIds(post);
  const pollData = normalizePollForEdit(post);

  postContent.value = String(initialText || '');
  selectedBackgroundKey.value = initialBackground || null;
  isPollEnabled.value = !!pollData;
  initialPollId.value = pollData?.id || null;
  pollOptions.value = pollData?.options?.slice?.(0, 10) || ['', '', ''];
  applyPollSettingsForContext({
    allowMultipleAnswers: !!pollData?.allowMultipleAnswers,
    allowAnyoneToAddOptions: !!pollData?.allowAnyoneToAddOptions,
    showResults: !!pollData?.showResults,
  });

  clearAllImages();

  const sourceAttachments = isPollEnabled.value
    ? []
    : (Array.isArray(post?.attachments) ? post.attachments : []);
  const sourceByFileId = new Map(
    sourceAttachments
      .map((att) => {
        const fileId = att?.fileId || att?.file_id || att?.mediaFileId || null;
        if (!fileId) return null;
        return [String(fileId), att];
      })
      .filter(Boolean)
  );

  attachedMedia.value = (isPollEnabled.value ? [] : existingFileIds).map((fileId) => {
    const source = sourceByFileId.get(String(fileId)) || null;
    const mediaKind = inferMediaKindFromAttachment(source);
    const thumb =
      source?.thumbnailUrl ||
      source?.posterUrl ||
      (mediaKind === 'video'
        ? `${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(fileId)}`
        : `${apiProxyUrl}/api/image/${encodeURIComponent(fileId)}`);

    return {
      fileId: String(fileId),
      previewUrl: thumb,
      permanentUrl: null,
      isExisting: true,
      mediaKind,
      thumbnailUrl: mediaKind === 'video' ? thumb : null,
      linkUrl: mediaKind === 'video' ? null : String(source?.linkUrl || source?.link_url || '').trim() || null,
      openInNewTab: source?.openInNewTab !== false && source?.open_in_new_tab !== false,
    };
  });

  initialExistingMediaFileIds.value = (isPollEnabled.value ? [] : existingFileIds).map(String);
  showScheduler.value = false;
  showMoreBackgrounds.value = false;
  showBackgroundArray.value = false;
  showEmojiPicker.value = false;
}

watch(
  () => [props.isOpen, props.mode, props.initialPost, props.startWithPoll],
  ([isOpenNow]) => {
    if (!isOpenNow) return;

    if (isEditMode.value) {
      hydrateEditComposer();
    } else {
      resetComposerState();
      if (props.startWithPoll) {
        enablePoll();
      }
    }

    nextTick(() => {
      syncComposerFromPostContent(false);
      autoExpand();
      positionPollSettingsMenu();
    });
  },
  { deep: true }
);

watch(
  () => [isCreatorPollAuthor.value, props.isOpen],
  () => {
    if (!isCreatorPollAuthor.value) {
      applyPollSettingsForContext(null);
      pollSettingsMenuOpen.value = false;
    }
  },
  { immediate: true }
);

watch(canUseBackgrounds, (allowed) => {
  if (!allowed) {
    selectedBackgroundKey.value = null;
    showBackgroundArray.value = false;
    showMoreBackgrounds.value = false;
  }
});

watch(
  () => hasSelectedBackground.value,
  () => {
    nextTick(() => {
      autoExpand();
    });
  }
);

watch(
  () => selectedBackground.value?.backgroundUrl || '',
  async (url) => {
    if (!url) {
      selectedBackgroundTone.value = 'dark';
      return;
    }
    selectedBackgroundTone.value = guessNewsfeedBackgroundTone(url);
    selectedBackgroundTone.value = await detectNewsfeedBackgroundTone(url);
  },
  { immediate: true }
);

function handleGlobalPointerDown(event) {
  if (!pollSettingsMenuOpen.value) return;
  const root = pollSettingsMenuRef.value;
  const dropdown = pollSettingsDropdownRef.value;
  if (!root) return;
  const target = event?.target;
  if (target instanceof Node && root.contains(target)) return;
  if (target instanceof Node && dropdown && dropdown.contains(target)) return;
  pollSettingsMenuOpen.value = false;
}

function handleViewportReposition() {
  positionPollSettingsMenu();
}

onMounted(() => {
  window.addEventListener('pointerdown', handleGlobalPointerDown);
  window.addEventListener('resize', handleViewportReposition);
  window.addEventListener('scroll', handleViewportReposition, true);
  nextTick(() => {
    if (composerRef.value) {
      composerRef.value.style.height = `${height.value}px`;
      syncComposerFromPostContent(false);
      autoExpand();
    }
  });
});

onUnmounted(() => {
  window.removeEventListener('pointerdown', handleGlobalPointerDown);
  window.removeEventListener('resize', handleViewportReposition);
  window.removeEventListener('scroll', handleViewportReposition, true);
  clearAllImages();
});



</script>

<style scoped>

/* ✅ Hover-only, nearly invisible scrollbar */
:deep(.chat-scroll) {
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: transparent transparent;
}

/* WebKit base */
:deep(.chat-scroll::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

/* ❌ Kill classic buttons (no-op for overlay, harmless otherwise) */
:deep(.chat-scroll::-webkit-scrollbar-button) {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
  background-color: transparent;
}

/* Track fully invisible */
:deep(.chat-scroll::-webkit-scrollbar-track) {
  background: transparent;
}

/* Thumb invisible by default */
:deep(.chat-scroll::-webkit-scrollbar-thumb) {
  background-color: transparent;
  border-radius: 999px;
}

/* ✅ On hover: fade scrollbar in */
:deep(.chat-scroll:hover) {
  scrollbar-color: rgba(203, 213, 225, 0.45) transparent; /* Firefox */
}

:deep(.chat-scroll:hover::-webkit-scrollbar-thumb) {
  background-color: rgba(203, 213, 225, 0.45);
}

/* Slightly stronger on thumb hover */
:deep(.chat-scroll:hover::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(203, 213, 225, 0.65);
}

.composer-editor {
  min-height: 96px;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.35;
  outline: none;
  color: rgb(39 39 42);
}

.composer-editor:empty::before {
  content: attr(data-placeholder);
  color: rgb(113 113 122);
  pointer-events: none;
}

.composer-editor--with-bg {
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: 600;
  padding-left: 2rem;
  padding-right: 2rem;
}

.composer-editor--with-bg:empty::before {
  display: block;
  width: 100%;
  text-align: center;
}

.composer-editor--tone-light {
  color: #ffffff;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.45);
}

.composer-editor--tone-light:empty::before {
  color: rgba(255, 255, 255, 0.88);
}

.composer-editor--tone-dark {
  color: #1f2937;
  text-shadow: 0 1px 8px rgba(255, 255, 255, 0.35);
}

.composer-editor--tone-dark:empty::before {
  color: rgba(31, 41, 55, 0.72);
}

:deep(.composer-editor a) {
  color: #2d8cff;
  font-weight: 500;
  text-decoration: none;
}

:deep(.composer-editor a:hover) {
  text-decoration: underline;
}

</style>
